import { isEmpty, isNil, uniq } from 'lodash';
import { createSelector, createStructuredSelector } from 'reselect';
import {
  CURRENT_YEAR,
  EARLY_WITHDRAWAL_PENALTY_AGE,
  EXCHANGE,
  EXCHANGE_DEFAULT_MAX,
  ExchangeType,
  PRIOR_YEAR,
  RETIREMENT,
  SystemCode,
  UNIT,
} from '../../../settings/settings';
import { formatUnixDate } from '../../../utils/dates/date-utility';
import {
  validateExchangeFromFund,
  validateExchangeToFund,
  validateExchangeToFundList,
} from '../../../utils/form/validate/validate-exchange';
import {
  updateCollegeExchangeFromFundNotesForFund,
  updateCollegeExchangeToFundNotesForFund,
} from '../../../utils/funds/college/college-exchange-fund-notes-utility';
import { updateSharedCollegeFundNotes } from '../../../utils/funds/college/college-fund-notes-utility';
import {
  updateExchangeFromFundNotesForFund,
  updateExchangeToFundNotesForFund,
} from '../../../utils/funds/exchange/exchange-fund-notes-utility';
import {
  agreementKey,
  calculateSellTransactionFundDetails,
  firstAvailableUncollectedSharesUnixDate,
  getExchangeFromFundLimits,
  isProductType529,
  isRetirementFund,
  isSelectedWithAmount,
  setCollegeExchangeSharePrice,
  setExchangeFromFundLimitMessage,
  setFundPendingTransaction,
} from '../../../utils/funds/funds-utility';
import {
  updateExchangeAddFundNotesForFund,
  updateSharedAddFundNotesForFund,
} from '../../../utils/funds/shared/add-fund-notes-utility';
import { checkFoundFeaturesByCode } from '../../../utils/funds/shared/shared-fund-notes-utility';
import {
  coalesce,
  currency,
  hasValue,
  stringPropSorter,
  sumArray,
  toLookup,
} from '../../../utils/utils';
import { getBucketRoll } from '../bucketRoll/bucketRoll.selectors';
import {
  getAllFundsList,
  getCollegeAllFundsMap,
} from '../funds/AllFunds/allFunds.selectors';
import {
  getIRALimitData,
  getIsFullyContributedIRA,
  getWithinIRAPriorYearContributionPeriod,
} from '../funds/IRA/IRA.selectors';
import { getPendingBuyExchangeSellTransactionRecordsByAgreementGrouped } from '../pendingTransactions/pendingTransactions.selectors';
import { getRedeemThresholds } from '../redeemThresholds/redeemThresholds.selectors';
import {
  getCoveredShares,
  getUncoveredShares,
} from '../sellTransaction/sellTransaction.selectors';
import { getUncollectedSharesLookup } from '../sellUncollectedShares/selectors';
import { getCostBasisMap as getCostBasisLookup } from '../costBasis/costBasis.selectors';
import { getCollegeFundsTowardsPlanMax } from '../shoppingCart/collegeShoppingCart.selectors';
import {
  getCurrentContributionYear,
  getFundBySelectedAgreementId,
  getPriorContributionYear,
  getQuickBuyAgreementID,
  getQuickBuyProductType,
  getUserFundList,
  getUserFundsRawList,
} from '../shoppingCart/shoppingCart.selectors';
import { getUserFundsRaw } from '../funds/UserFunds/userFunds.selectors';
import {
  isAPP,
  isAPPorMF,
  isCompatibleIraRegistration,
} from '../../../utils/advice/advice-utility';
import { getExchangeToTransactionLimits } from '../transactionLimits/transactionLimits.selectors';
import { getPersonalInformationData } from '../user/PersonalInformation/personalInformation.selectors';
import {
  getRegistrationTypeInfoWithProductTypes,
  getSelectedRegistrationType,
  getUserRegistrationTypeList,
} from '../user/UserRegistrationTypes/UserRegistrationTypes.selectors';
import {
  getAcceptedMarketFluctuationDisclosure,
  getExchangeContributionYear,
  getExchangeFederalWithholdingPercent,
  getExchangeFromLookup,
  getExchangeToExistingAllowedAgreementIdLookup,
  getExchangeToLookup,
  getExchangeToNewAllowedTickerLookup,
  getExchangeToNewFundList,
  getExchangeType,
  getExchangeFederalWithholdingApplied,
} from './selectors';
import { isExchange } from '../../../utils/transactions-util';
import {
  isCustomerWebViewEnabled,
  isSalesforceRep,
} from '../authentication/authentication.module.selectors';
import { appendBasePath } from '../../../utils/nav.utils';
import { Paths } from '../../../components/Navigation/BreadCrumbs/types';
import { isExchangeMaxLimitEnabled } from '../../../utils/FeatureFlag';
export * from './selectors';

/**
 * @typedef {object} ExchangeTransactionItem individual item in the exchange transaction.
 * @property {boolean} selected If item is currently selected. Non-selected items can be in the transaction to preserve
 * previously entered amounts in case they are re-selected.
 * @property {number} grossAmount
 * @property {string} grossUnit
 *
 * WebStorm isn't mixing in ExchangeTransactionItem to ExchangeFund, so above props duplicated
 * in ExchangeFund below.
 */

/**
 * @typedef {Object.<string, ExchangeTransactionItem>} ExchangeTransactionLookup Exchange transaction lookup object, key is a fund agreementID
 */

/**
 * Exchange fund sorter sorts non-retirement first, retirement second, and then by same sort order that buy uses
 * @param {UserFund} x
 * @param {UserFund} y
 * @returns {number} -1, 0, 1
 */
function exchangeFundSorter(x, y) {
  const { displayClassification: xDisplayClassification, sortRank: xSortRank } =
    x;
  const { displayClassification: yDisplayClassification, sortRank: ySortRank } =
    y;

  return xDisplayClassification === yDisplayClassification
    ? xSortRank - ySortRank
    : xDisplayClassification === 'RETIREMENT'
    ? -1
    : 1;
}

/**
 * @typedef {UserFund} ExchangeFund
 * @property {boolean} isIRAContribution If exchanging into this fund represents an IRA contribution (to is IRA, from is not)
 * WebStorm doesn't support at-mixes, so duplicating props here:
 *
 * @mixes ExchangeTransactionItem
 * @property {boolean} selected If item is currently selected. Non-selected items can be in the transaction to preserve
 * previously entered amounts in case they are re-selected.
 * @property {number} grossAmount
 * @property {string} grossUnit
 *
 * From server, move to UserFund...:
 *
 * @property {number} equityAsOf Subset of total balance that is available to sell
 * @property {Array} chargeables Object with information on redemption fees due upon sale, if applicable
 * @property {number} chargeables[].shareQuantity Quantity of shares subject to redemption fee
 * @property {string} chargeables[].chargeableSharesReleaseDate Date that the chargeable shares are no longer chargeable
 * @property {number} shares Number of shares the user has
 * @property {number} sharePrice
 * @property {number} closeoutFee
 *
 * Validation:
 *
 * @property {string} grossAmountError Error message if gross amount has a problem
 */
/**
 * Returns list of funds that can be exchanged from with exchange transaction merged in, amounts calculated,
 * and validated.
 *
 * @function
 * @param state
 * @returns {ExchangeFund[]}
 */
export const getExchangeFromFundList = createSelector(
  isSalesforceRep,
  isCustomerWebViewEnabled,
  getCollegeAllFundsMap,
  getCostBasisLookup,
  getExchangeFromLookup,
  getPendingBuyExchangeSellTransactionRecordsByAgreementGrouped,
  getPersonalInformationData,
  getQuickBuyAgreementID,
  getRedeemThresholds,
  getUncollectedSharesLookup,
  getUserFundList,
  getQuickBuyProductType,
  getFundBySelectedAgreementId,
  (
    isSalesforceRep,
    customerWebViewEnabled,
    collegeAllFundsMap,
    costBasisLookup,
    exchangeFromLookup,
    pendingBuyExchangeSellTransactionRecordsByAgreementGrouped,
    personalInfo,
    quickBuyAgreementID,
    redeemThresholds,
    uncollectedSharesLookup,
    userFundList,
    quickBuyProductType,
    fundBySelectedAgreementId
  ) => {
    const filteredList =
      quickBuyAgreementID && isAPP(quickBuyProductType)
        ? userFundList.filter(
            (fund) =>
              fundBySelectedAgreementId.length &&
              fundBySelectedAgreementId[0].agreementID !== fund.agreementID &&
              isCompatibleIraRegistration(
                fundBySelectedAgreementId[0].agreementType,
                fund.agreementType
              ) &&
              isAPPorMF(fund.productType)
          )
        : quickBuyAgreementID
        ? userFundList.filter(
            (fund) => fund.agreementID === quickBuyAgreementID
          )
        : userFundList;
    if (
      userFundList.length &&
      !filteredList.length &&
      quickBuyAgreementID &&
      isAPP(quickBuyProductType)
    ) {
      // If the filtered fund list is empty, then the client cannot
      // perform an exchange with the selected APP fund.
      // Kicking them back to the general exchange flow.
      console.log(
        'No eligible funding options for selected account. Redirecting to general exchange.'
      );
      window.location.href = appendBasePath(Paths.EXCHANGE);
    }
    return filteredList
      .map((sourceFund) => {
        const {
          agreementID,
          agreementNumber,
          currentYearLimit = [],
          balanceAsOf,
          chargeables,
          displayClassification,
          equityAsOf,
          trpCode,
        } = sourceFund;
        const exchangeTransactionItem = exchangeFromLookup[agreementID] || {};
        const hasUncollectedShares = balanceAsOf !== equityAsOf;
        const redeemableThresholdDollarPercentage = coalesce(
          redeemThresholds[trpCode],
          100
        );
        const uncollectedShares = uncollectedSharesLookup[agreementID] || {};
        const settlementDate =
          firstAvailableUncollectedSharesUnixDate(uncollectedShares);
        const costBasisData =
          costBasisLookup[agreementKey(agreementID, agreementNumber)] || {};

        /** @type ExchangeFund */
        const fund = {
          ...sourceFund,
          notes: {},
          disabled: false,
          bankAssociated: true,
          hasUncollectedShares,
          chargeableShareQuantity: sumArray(
            chargeables,
            (c) => c.shareQuantity,
            0
          ),
          coveredShares: getCoveredShares(costBasisData),
          hasEarlyWithdrawalPenalty:
            personalInfo.age < EARLY_WITHDRAWAL_PENALTY_AGE &&
            displayClassification === RETIREMENT,
          hasUncollectedSharesDate: hasUncollectedShares && settlementDate,
          uncollectedSharesQuantity: sumArray(
            uncollectedShares,
            (uncollectedShare) => uncollectedShare.shares,
            0
          ),
          redeemableThresholdDollarPercentage,
          redeemableFundDollars:
            Math.round(redeemableThresholdDollarPercentage * balanceAsOf) / 100,
          settlementDate,
          transactionLimit: getExchangeFromFundLimits(currentYearLimit),
          uncoveredShares: getUncoveredShares(costBasisData),
          ...exchangeTransactionItem,
        };

        setCollegeExchangeSharePrice(fund, collegeAllFundsMap);
        setExchangeFromFundLimitMessage(fund);
        calculateSellTransactionFundDetails(fund, undefined, true);
        validateExchangeFromFund(fund);
        setFundPendingTransaction(
          fund,
          pendingBuyExchangeSellTransactionRecordsByAgreementGrouped
        );
        updateExchangeFromFundNotesForFund(fund, personalInfo);
        return fund;
      })
      .sort(exchangeFundSorter)
      .filter(
        (fund) =>
          ((!customerWebViewEnabled && isSalesforceRep) ||
            !fund.hiddenFromOnlineCustomer) &&
          !fund.viewOnly
      );
  }
);

/**
 * Identifies if Exchange-from funds selection ready to proceed: no selected funds have errors and at least one
 * selected funds have an amount.
 * @function
 * @param  state
 * @returns {boolean}
 */
export const getExchangeFromIsValid = createSelector(
  getExchangeFromFundList,
  (exchangeFromFundList) => {
    const exchangeAmount = exchangeFromFundList
      .filter((fund) => fund.selected)
      .map((fund) => fund.grossAmountDollars);
    const exchangeAmountTotal = exchangeAmount.reduce((a, b) => a + b, 0);
    return (
      exchangeFromFundList.every(
        (fund) => !fund.selected || isNil(fund.grossAmountError)
      ) &&
      exchangeFromFundList.some(isSelectedWithAmount) &&
      (isExchangeMaxLimitEnabled()
        ? exchangeFromFundList.every(
            (fund) => exchangeAmountTotal <= fund.transactionLimit.maximum
          )
        : exchangeAmountTotal <= EXCHANGE_DEFAULT_MAX)
    );
  }
);

/**
 * Returns the list of funds we're exchanging from, meaning only those that are selected with an amount.
 *
 * @function
 * @param state
 * @returns {ExchangeFund[]}
 */
export const getExchangeFromSummaryFundList = createSelector(
  getExchangeFromFundList,
  (exchangeFromFundList) => exchangeFromFundList.filter(isSelectedWithAmount)
);

/**
 * Returns the first selected "from" fund with an amount
 */
export const getExchangeFromFirstFund = createSelector(
  getExchangeFromSummaryFundList,
  (exchangeFromSummaryFundList) =>
    hasValue(exchangeFromSummaryFundList)
      ? exchangeFromSummaryFundList[0]
      : null
);

/**
 * @type {OutputSelector<unknown, boolean, (res: (*|null)) => boolean>}
 *
 * Returns if the user can exchange into new based on the funds in the selected registration group
 */
export const isExchangeToNewEligible = createSelector(
  getExchangeFromFirstFund,
  getUserFundList,
  (exchangeSelectedFund, userFunds) => {
    const registrationGroup = exchangeSelectedFund?.registrationGroup;
    const filteredUserFunds = userFunds.filter(
      (fund) => fund.registrationGroup === registrationGroup
    );
    return filteredUserFunds.length > 0;
  }
);

/**
 * Returns the selected from funds are retirement funds
 * @function
 * @param state
 * @returns {boolean}
 */
export const getExchangeFromIsIRA = createSelector(
  getExchangeFromFirstFund,
  isRetirementFund
);

/**
 * Returns the overall unit for the "from" selected funds based on how many funds are selected and their
 * respective from unit, with a mixture of units always converted to shares.
 *
 * @function
 * @param state
 * @returns {string} UNIT.DOLLARS, UNIT.PERCENT, UNIT.SHARES, or UNIT.ALL_SHARES
 */
export const getExchangeFromUnit = createSelector(
  getExchangeFromSummaryFundList,
  (exchangeFromSummaryFundList) => {
    switch (exchangeFromSummaryFundList.length) {
      case 0:
        return UNIT.DOLLARS;

      case 1:
        return exchangeFromSummaryFundList[0].grossUnit;

      default:
        break;
    }

    const uniqUnits = uniq(
      exchangeFromSummaryFundList.map((fund) => fund.grossUnit)
    );
    return uniqUnits.length === 1 ? uniqUnits[0] : UNIT.SHARES; // whenever we have a mix of units, we normalize to shares
  }
);

/**
 * Gets the total dollar amount of the selected from funds, regardless of actual source unit.
 *
 * @function
 * @param state
 * @returns {number}
 */
export const getExchangeFromTotalDollars = createSelector(
  getExchangeFromSummaryFundList,
  (exchangeFromSummaryFundList) =>
    sumArray(exchangeFromSummaryFundList, (fund) => fund.grossAmountDollars)
);

/**
 * Returns a boolean indicating if the exchange from fund is less than the IRA
 * contribution limits.
 * @function
 * @param {object} state
 * @returns {boolean}
 */
export const getIsExchangeAmountWithinIRAContributionLimits = createSelector(
  getIRALimitData,
  getExchangeFromIsIRA,
  getExchangeFromTotalDollars,
  getWithinIRAPriorYearContributionPeriod,
  /**
   * @param {object} IRALimitData
   * @param {boolean} exchangeFromIsIRA
   * @param {number} exchangeFromTotalDollars
   * @param {boolean} withinIRAPriorYearContributionPeriod
   * @returns {boolean}
   */
  (
    IRALimitData,
    exchangeFromIsIRA,
    exchangeFromTotalDollars,
    withinIRAPriorYearContributionPeriod
  ) =>
    exchangeFromIsIRA ||
    exchangeFromTotalDollars <=
      IRALimitData.currentYearRemainingContributions ||
    (withinIRAPriorYearContributionPeriod &&
      exchangeFromTotalDollars <= IRALimitData.priorYearRemainingContributions)
);

function getExchangeYearIRABlockedMessage(yearPrefix) {
  return createSelector(
    getCurrentContributionYear,
    getIRALimitData,
    getExchangeFromIsIRA,
    getExchangeFromTotalDollars,
    getPriorContributionYear,
    getWithinIRAPriorYearContributionPeriod,
    /**
     * @param {number} currentContributionYear
     * @param {object} IRALimitData
     * @param {boolean} exchangeFromIsIRA
     * @param {number} exchangeFromTotalDollars
     * @param {number} priorContributionYear
     * @param {boolean} withinIRAPriorYearContributionPeriod
     * @returns {string|null}
     */
    (
      currentContributionYear,
      IRALimitData,
      exchangeFromIsIRA,
      exchangeFromTotalDollars,
      priorContributionYear,
      withinIRAPriorYearContributionPeriod
    ) => {
      if (exchangeFromIsIRA) {
        return null;
      }
      if (!withinIRAPriorYearContributionPeriod && yearPrefix === PRIOR_YEAR) {
        // will not happen in planned use cases, but check just in case
        return `Contributions to ${priorContributionYear} are no longer available.`;
      }

      const remainingContributions =
        IRALimitData[`${yearPrefix}YearRemainingContributions`];
      const yearNumber =
        yearPrefix === PRIOR_YEAR
          ? priorContributionYear
          : currentContributionYear;

      if (remainingContributions <= 0) {
        return `Great job! You’ve reached the maximum ${yearNumber} IRA contribution.`;
      }
      if (exchangeFromTotalDollars > remainingContributions) {
        return `Contribution amount ${currency(
          exchangeFromTotalDollars
        )} exceeds ${currency(
          remainingContributions
        )} remaining ${yearNumber} IRA contribution limit.`;
      }

      return null;
    }
  );
}

/**
 * Returns a user-friendly error message if exchanging to the current year IRA is blocked.
 * @function
 * @param state
 * @returns {string}null}
 */
export const getExchangeCurrentYearIRABlockedMessage =
  getExchangeYearIRABlockedMessage(CURRENT_YEAR);

/**
 * Returns a user-friendly error message if exchanging to the prior year IRA is blocked.
 * @function
 * @param state
 * @returns {string}null}
 */
export const getExchangePriorYearIRABlockedMessage =
  getExchangeYearIRABlockedMessage(PRIOR_YEAR);

/**
 * Returns the amount of remaining IRA contributions for the exchange
 * TODO modify this after prior/current year story is done
 * @param {object} state
 * @returns {number}
 */
export const getExchangeIRALimitAmount = createSelector(
  getIRALimitData,
  /**
   * @param {object} IRALimitData
   */
  (IRALimitData) => IRALimitData.currentYearRemainingContributions
);

/**
 * Gets the transaction limits for a newly added fund based on registration type
 * @param {object} state
 * @returns {object}
 */
export const getExchangeToNewFundTransactionLimits = createSelector(
  getExchangeFromFirstFund,
  getUserRegistrationTypeList,
  (exchangeFromFirstFund, userRegistrationTypeList) => {
    const { uniqueKey } = exchangeFromFirstFund || {};
    const registrationGroup =
      Array.isArray(userRegistrationTypeList) &&
      userRegistrationTypeList.find((ele) =>
        ele.modelAgreementUniqueIDs.some(
          (modelAgreementID) => modelAgreementID === uniqueKey
        )
      );
    const limit = registrationGroup?.currentYearLimit?.find(
      (ele) => ele.type === EXCHANGE
    );
    return limit;
  }
);

/**
 * Returns list of funds that can be exchanged from with exchange transaction merged in, amounts calculated,
 * and validated.
 *
 * @function
 * @param {object} state
 * @returns {ExchangeFund[]}
 */
export const getExchangeToFundList = createSelector(
  isSalesforceRep,
  isCustomerWebViewEnabled,
  getBucketRoll,
  getCostBasisLookup,
  getCollegeFundsTowardsPlanMax,
  getExchangeFromFirstFund,
  getExchangeFromIsIRA,
  getExchangeFromLookup,
  getExchangeFromTotalDollars,
  getExchangeFromUnit,
  getExchangeToExistingAllowedAgreementIdLookup,
  getExchangeToLookup,
  getExchangeToNewAllowedTickerLookup,
  getExchangeToNewFundList,
  getExchangeToNewFundTransactionLimits,
  getExchangeToTransactionLimits,
  getExchangeType,
  getIRALimitData,
  getIsExchangeAmountWithinIRAContributionLimits,
  getIsFullyContributedIRA,
  getPersonalInformationData,
  getUserFundList,
  getQuickBuyAgreementID,
  getQuickBuyProductType,
  getFundBySelectedAgreementId,
  getExchangeFromSummaryFundList,
  (
    isSalesforceRep,
    customerWebViewEnabled,
    bucketRoll,
    costBasisLookup,
    collegeFundsTowardsPlanMax,
    exchangeFromFirstFund,
    exchangeFromIsIRA,
    exchangeFromLookup,
    exchangeFromTotalDollars,
    exchangeFromUnit,
    exchangeToExistingAllowedAgreementIdLookup,
    exchangeToLookup,
    exchangeToNewAllowedTickerLookup,
    exchangeToNewFundList,
    exchangeToNewFundTransactionLimits,
    exchangeToTransactionLimits,
    exchangeType,
    IRALimitData,
    isExchangeAmountWithinIRAContributionLimits,
    isFullyContributedIRA,
    personalInformationData,
    userFundList,
    quickBuyAgreementID,
    quickBuyProductType,
    fundBySelectedAgreementId,
    exchangeFromSummaryFundList
  ) => {
    const filteredFundList = (
      quickBuyAgreementID &&
      isAPP(quickBuyProductType) &&
      fundBySelectedAgreementId.length
        ? userFundList.filter((fund) => {
            if (fund.agreementID === quickBuyAgreementID) {
              //pre-select given APP agreement ID
              fund.selected = true;
              return true;
            }
            return false;
          })
        : userFundList
            .filter(({ agreementID }) =>
              exchangeFromLookup[agreementID]
                ? !exchangeFromLookup[agreementID].selected
                : true
            )
            .concat(
              exchangeToNewFundList.filter(
                ({ ticker, investmentVehicleKey: { trpcode } = {} }) => {
                  return (
                    exchangeToNewAllowedTickerLookup[ticker] ||
                    exchangeToNewAllowedTickerLookup[trpcode]
                  );
                }
              )
            )
            .map((sourceFund) => {
              const { agreementID, agreementNumber } = sourceFund;
              const exchangeTransactionItem =
                exchangeToLookup[agreementID] || {};
              const costBasisData =
                costBasisLookup[agreementKey(agreementID, agreementNumber)] ||
                {};
              const fund = {
                ...sourceFund,
                disabled: false,
                bankAssociated: true,
                coveredShares: getCoveredShares(costBasisData),
                uncoveredShares: getUncoveredShares(costBasisData),
                isIRAContribution:
                  !exchangeFromIsIRA && isRetirementFund(sourceFund),
                ...exchangeTransactionItem,
                ...IRALimitData,
                notes: {},
                canExchange:
                  exchangeToExistingAllowedAgreementIdLookup[agreementID] &&
                  (exchangeFromLookup[agreementID]
                    ? !exchangeFromLookup[agreementID].selected
                    : true),
              };

              validateExchangeToFund(
                fund,
                isExchangeAmountWithinIRAContributionLimits,
                isFullyContributedIRA,
                collegeFundsTowardsPlanMax
              );

              if (isProductType529(fund.productType)) {
                updateSharedCollegeFundNotes(fund, personalInformationData);
                updateCollegeExchangeToFundNotesForFund(
                  fund,
                  personalInformationData
                );
              } else {
                const selectedFund =
                  exchangeFromSummaryFundList && exchangeFromSummaryFundList[0];
                updateExchangeToFundNotesForFund(
                  fund,
                  personalInformationData,
                  exchangeFromFirstFund,
                  bucketRoll.nextYearContributionAmountAvailable,
                  selectedFund
                );
              }
              return fund;
            })
            .sort(exchangeFundSorter)
    ).filter(
      (fund) =>
        ((!customerWebViewEnabled && isSalesforceRep) ||
          !fund.hiddenFromOnlineCustomer) &&
        !fund.viewOnly
    );
    return validateExchangeToFundList(
      exchangeFromFirstFund,
      exchangeFromTotalDollars,
      exchangeType,
      filteredFundList,
      exchangeToTransactionLimits,
      exchangeToNewFundTransactionLimits
    );
  }
);

/**
 * Returns the list of funds we're exchanging to. This is more complicated than the from selections. If we're
 * exchanging 1:1 or M:1 than we only need a fund selected and no other info. If we're exchanging 1:M then we
 * need selected funds to have an amount entered OR have all remaining selected.
 *
 * @function
 * @param state
 * @returns {ExchangeFund[]}
 */
export const getExchangeToSummaryFundList = createSelector(
  getExchangeToFundList,
  getExchangeType,
  (exchangeToFundList, exchangeType) =>
    exchangeToFundList.filter(
      (fund) =>
        fund.selected &&
        !fund.disabled &&
        (exchangeType !== ExchangeType.ONE_TO_MANY ||
          parseInt(fund.grossAmount) ||
          fund.grossUnit === UNIT.ALL_REMAINING)
    )
);

/**
 * Determines if market fluctuation checkbox should be shown for exchange
 * given the from fund selection, if the amount is within contribution limits,
 * and if the to fund is retirement
 * @function
 * @param state
 * @return {boolean}
 */
export const getExchangeShowMarketFluctuationDisclosure = createSelector(
  getExchangeFromUnit, // UNIT.DOLLARS, UNIT.PERCENT, UNIT.SHARES, or UNIT.ALL_SHARES
  getExchangeFromIsIRA,
  getIsExchangeAmountWithinIRAContributionLimits,
  /**
   * @param {string} exchangeFromUnit
   * @param {boolean} exchangeFromIsIRA
   * @param {boolean} isExchangeAmountWithinIRAContributionLimits
   * @return {boolean}
   */
  (
    exchangeFromUnit,
    exchangeFromIsIRA,
    isExchangeAmountWithinIRAContributionLimits
  ) =>
    !exchangeFromIsIRA &&
    isExchangeAmountWithinIRAContributionLimits &&
    exchangeFromUnit !== UNIT.DOLLARS
);

/**
 * Returns true if market fluctuation disclosure does not apply or it is applies and has been accepted.
 * @param state
 * @returns {boolean}
 */
export const getAcceptedMarketFluctuationDisclosureApplied = createSelector(
  getAcceptedMarketFluctuationDisclosure,
  getExchangeShowMarketFluctuationDisclosure,
  getExchangeToSummaryFundList,
  /**
   * @param {boolean} acceptedMarketFluctuationDisclosure
   * @param {boolean} exchangeShowMarketFluctuationDisclosure
   * @param {ExchangeFund[]} exchangeToSummaryFundList
   * @returns {boolean}
   */
  (
    acceptedMarketFluctuationDisclosure,
    exchangeShowMarketFluctuationDisclosure,
    exchangeToSummaryFundList
  ) =>
    !exchangeToSummaryFundList.some((fund) => isRetirementFund(fund)) ||
    !exchangeShowMarketFluctuationDisclosure ||
    acceptedMarketFluctuationDisclosure
);

/**
 * Identifies if the exchange transaction is taxable (exchanging from IRA to retail)
 *
 * @function
 * @param state
 * @returns {boolean}
 */
export const getExchangeIsIRAContribution = createSelector(
  getExchangeFromSummaryFundList,
  getExchangeToSummaryFundList,
  (exchangeFromSummaryFundList, exchangeToSummaryFundList) =>
    hasValue(exchangeFromSummaryFundList) &&
    hasValue(exchangeToSummaryFundList) &&
    !isRetirementFund(exchangeFromSummaryFundList[0]) &&
    isRetirementFund(exchangeToSummaryFundList[0])
);

/**
 * Identifies if the exchange transaction is taxable (exchanging from IRA to retail)
 *
 * @function
 * @param state
 * @returns {boolean}
 */
export const getExchangeIsTaxable = createSelector(
  getExchangeFromSummaryFundList,
  getExchangeToSummaryFundList,
  (exchangeFromSummaryFundList, exchangeToSummaryFundList) =>
    hasValue(exchangeFromSummaryFundList) &&
    hasValue(exchangeToSummaryFundList) &&
    isRetirementFund(exchangeFromSummaryFundList[0]) &&
    !isRetirementFund(exchangeToSummaryFundList[0])
);

/**
 * Identifies if the exchange is to a RI2020 fund for the first time within an IRA;
 *
 * @function
 * @param state
 * @returns {boolean}
 */
export const getExchangeShowRIWithholdingDisclosure = createSelector(
  getExchangeToSummaryFundList,
  (exchangeToSummaryFundList) =>
    exchangeToSummaryFundList.some(
      (fund) =>
        fund.newFund && fund.ticker === 'TRLAX' && isRetirementFund(fund)
    )
);

/**
 *
 * @param state
 * @returns {boolean}
 */
export const getAcceptedRI2020WithholdingDisclosure = (state) =>
  state.exchangeTransaction.acceptedRI2020WithholdingDisclosure;

/**
 * Returns true if RI2020 disclosure does not apply or if it applies and has been accepted.
 * @param state
 * @returns {boolean}
 */
export const getAcceptedRI2020WithholdingDisclosureApplied = createSelector(
  getAcceptedRI2020WithholdingDisclosure,
  getExchangeShowRIWithholdingDisclosure,
  (acceptedRI2020WithholdingDisclosure, exchangeShowRIWithholdingDisclosure) =>
    !exchangeShowRIWithholdingDisclosure || acceptedRI2020WithholdingDisclosure
);

/**
 * @param state
 * @returns {string}
 */
export const getSingleExchangeFederalWithholdingApplied = createSelector(
  (state) => state.exchangeTransaction.exchangeFederalWithholdingApplied,
  getExchangeIsTaxable,
  (exchangeIsTaxable, exchangeFederalWithholdingApplied) =>
    exchangeIsTaxable && exchangeFederalWithholdingApplied
);

export const getSingleExchangeFederalWithholdingDollars = createSelector(
  getSingleExchangeFederalWithholdingApplied,
  (state) => state.exchangeTransaction.exchangeFederalWithholdingPercent,
  getExchangeFromTotalDollars,
  (
    exchangeFederalWithholdingApplied,
    exchangeFederalWithholdingPercent,
    exchangeFromTotalDollars
  ) =>
    exchangeFederalWithholdingApplied &&
    exchangeFederalWithholdingPercent &&
    exchangeFromTotalDollars
      ? Math.round(
          exchangeFromTotalDollars * exchangeFederalWithholdingPercent
        ) / 100
      : 0
);

/**
 * Calculates the federal withholding for the entire transaction in dollars.
 *
 * @function
 * @param state
 * @returns {number}
 */
export const getExchangeFederalWithholdingDollars = createSelector(
  getExchangeFederalWithholdingApplied,
  getExchangeFederalWithholdingPercent,
  getExchangeFromTotalDollars,
  (
    exchangeFederalWithholdingApplied,
    exchangeFederalWithholdingPercent,
    exchangeFromTotalDollars
  ) =>
    exchangeFederalWithholdingApplied &&
    exchangeFederalWithholdingPercent &&
    exchangeFromTotalDollars
      ? Math.round(
          exchangeFromTotalDollars * exchangeFederalWithholdingPercent
        ) / 100
      : 0
);

/**
 * Determines if an exchange has only retirement funds
 * @function
 * @param state
 * @returns {boolean}
 */
export const getExchangeHasOnlyRetirementFunds = createSelector(
  getExchangeToFundList,
  (exchangeToFundList) =>
    exchangeToFundList &&
    exchangeToFundList.every((fund) => isRetirementFund(fund))
);

/**
 * Gets the as of date for balances
 * @param state
 * @returns {Date}
 */
export const getExchangeAsOfDate = createSelector(getUserFundsRaw, (funds) =>
  funds.length ? formatUnixDate(funds[0].balanceAsOfDate) : ''
);

/**
 * Identifies if Exchange-to funds selection ready to proceed: has summary funds without errors
 *
 * @function
 * @param  state
 * @returns {boolean}
 */
export const getExchangeToIsValid = createSelector(
  getAcceptedMarketFluctuationDisclosureApplied,
  getExchangeContributionYear,
  getExchangeIsIRAContribution,
  getExchangeToSummaryFundList,
  getExchangeType,
  getWithinIRAPriorYearContributionPeriod,
  (
    acceptedMarketFluctuationDisclosureApplied,
    exchangeContributionYear,
    exchangeIsIRAContribution,
    exchangeToSummaryFundList,
    exchangeType,
    withinIRAPriorYearContributionPeriod
  ) => {
    const result =
      acceptedMarketFluctuationDisclosureApplied &&
      exchangeToSummaryFundList?.every((fund) =>
        isNil(fund.grossAmountError)
      ) &&
      (exchangeType === ExchangeType.ONE_TO_MANY ||
        exchangeToSummaryFundList.length === 1) &&
      (!withinIRAPriorYearContributionPeriod ||
        !exchangeIsIRAContribution ||
        exchangeContributionYear);
    return result;
  }
);

/**
 * Returns if the exchange transaction is exchanging to a new fund
 * Used to determine showing trusted contact tile on confirmation page
 * @param state
 * @returns {Boolean}
 */
export const getExchangeToContainsNewFund = createSelector(
  getExchangeToNewFundList,
  getExchangeToLookup,
  /**
   * @param {AvailableFund[]}
   * @param {ExchangeTransactionLookup[]}
   */
  (exchangeToNewFundList, exchangeToLookup) =>
    Object.values(exchangeToLookup).some((fund) =>
      exchangeToNewFundList.some(
        (newFund) => newFund.agreementID === fund.agreementID
      )
    )
);

export const getHasExchangeFromSweep = createSelector(
  getExchangeFromSummaryFundList,
  (exchangeFromSummaryFundList) =>
    exchangeFromSummaryFundList.some((fund) => fund.sweep)
);

/**
 * Returns list of step 1 funds (FROM) with interested party flag
 * @param state
 * @returns {ExchangeFund[]}
 */
export const getExchangeFundsWithInterestedPartyFlag = createSelector(
  getExchangeFromSummaryFundList,
  (getExchangeFromSummaryFundList) =>
    getExchangeFromSummaryFundList.filter((fund) =>
      checkFoundFeaturesByCode(
        fund,
        SystemCode.CONFIRM_TO_INTRSTD_PARTY_WARNING,
        true
      )
    )
);

/**
 * Returns list of newly added funds in step 2 (TO)
 * @param state
 * @returns {ExchangeFund[]}
 */
export const getExchangeToNewFunds = createSelector(
  getExchangeToSummaryFundList,
  (exchangeToSummaryFundList) =>
    exchangeToSummaryFundList.filter((fund) => fund.newFund)
);

/**
 * Returns true/false indicating if the exchange should show the interested party disclaimer
 * Must have funds that contain interested party flag and must be exchanging to new
 * @param state
 * @returns {boolean}
 */
export const getExchangeHasInterestedParty = createSelector(
  getExchangeFundsWithInterestedPartyFlag,
  getExchangeToNewFunds,
  (exchangeFundsWithInterestedPartyFlag, exchangeToNewFunds) =>
    exchangeFundsWithInterestedPartyFlag.length && exchangeToNewFunds.length
);

/**
 * Gets the college exchange from fund list with 529 specific notes
 * @param state
 * @returns {ExchangeFund[]}
 */
export const getCollegeExchangeFromFundList = createSelector(
  getExchangeFromFundList,
  getPersonalInformationData,
  (exchangeFromFundList, personalInformationData) =>
    exchangeFromFundList.map((origFund) => {
      const fund = {
        ...origFund,
        notes: {},
      };
      updateCollegeExchangeFromFundNotesForFund(fund, personalInformationData);
      return fund;
    })
);

/**
 * Gets the college exchange from fund list with 529 specific notes
 * @param state
 * @returns {ExchangeFund[]}
 */
export const getCollegeExchangeToFundList = createSelector(
  getExchangeToFundList,
  getPersonalInformationData,
  (exchangeToFundList, personalInformationData) =>
    exchangeToFundList
      .map((origFund) => {
        const fund = {
          ...origFund,
          disabled: false,
          notes: {},
        };
        updateCollegeExchangeToFundNotesForFund(fund, personalInformationData);
        return fund;
      })
      .filter((fund) => fund.canExchange || fund.newFund)
);

/**
 * Returns the exchange from fund list with 529 funds filtered out
 * @param state
 * @returns {ExchangeFund[]}
 */
export const getExchangeFromFundListWithout529 = createSelector(
  getExchangeFromFundList,
  (exchangeFromFundList) =>
    exchangeFromFundList.filter(
      (fund) =>
        fund.productType === 'MutualFund' ||
        (isAPP(fund.productType) && isExchange())
    )
);

export const getCollegeExchangeToNewFundList = createSelector(
  [
    getCollegeAllFundsMap,
    getExchangeToNewFundList,
    getSelectedRegistrationType,
    getUserFundsRawList,
  ],
  (
    collegeAllFundsMap,
    exchangeToNewFundList,
    selectedRegistrationType,
    userFundsRawList
  ) => {
    if (isEmpty(selectedRegistrationType) || isEmpty(collegeAllFundsMap)) {
      return [];
    }

    const modelAgreementUniqueIDs = Array.isArray(selectedRegistrationType)
      ? selectedRegistrationType.reduce(
          (ids, registration) =>
            ids.concat(registration.modelAgreementUniqueIDs),
          []
        )
      : selectedRegistrationType.modelAgreementUniqueIDs;

    if (isEmpty(modelAgreementUniqueIDs)) {
      return [];
    }

    const modelAgreementUniqueID = modelAgreementUniqueIDs.find((id) =>
      hasValue(collegeAllFundsMap[id])
    );
    if (!modelAgreementUniqueID) {
      return [];
    }

    const userFundByUniqueKey = toLookup(
      userFundsRawList,
      (fund) => fund.uniqueKey
    );

    const updatedRegistrationType = {
      ...selectedRegistrationType,
      existingTickers: modelAgreementUniqueIDs.map(
        (uniqueID) => (userFundByUniqueKey[uniqueID] || {}).fundName
      ),
    };

    const allFunds = collegeAllFundsMap[modelAgreementUniqueID];
    return allFunds
      .map((fund) => {
        const newFund = {
          ...fund,
          disabled: false,
          ticker: fund.name,
          notes: {},
        };
        updateSharedAddFundNotesForFund(
          newFund,
          exchangeToNewFundList,
          updatedRegistrationType
        );
        return newFund;
      })
      .sort(stringPropSorter((fund) => fund.name));
  }
);

export const getSelectedExchangeFund = createSelector(
  getExchangeFromFirstFund,
  getExchangeToFundList,
  getExchangeType,
  (exchangeFromFirstFund, exchangeToFundList, exchangeType) => {
    const selectedFund = exchangeToFundList.find((fund) => fund.selected);
    return exchangeType === ExchangeType.ONE_TO_MANY && selectedFund
      ? selectedFund
      : exchangeFromFirstFund;
  }
);

export const getCollegeExchangeRegistrationTypeInfo = createSelector(
  getRegistrationTypeInfoWithProductTypes,
  getSelectedExchangeFund,
  (registrationTypeInfoWithProductTypes, selectedExchangeFund) => {
    registrationTypeInfoWithProductTypes.selectedRegistrationType.productType =
      selectedExchangeFund.productType;
    registrationTypeInfoWithProductTypes.selectedRegistrationType.registrationGroup =
      selectedExchangeFund.registrationGroup;
    return registrationTypeInfoWithProductTypes;
  }
);

export const getExchangeRegistrationTypeInfo = createSelector(
  [getSelectedExchangeFund, getUserRegistrationTypeList],
  (selectedExchangeFund, userRegistrationTypeList) => {
    const {
      agreementType,
      displayClassification,
      formattedEntityNames,
      productType,
      registrationGroup,
    } = selectedExchangeFund;

    const selectedFundRegistrationType =
      !isEmpty(userRegistrationTypeList) &&
      userRegistrationTypeList.find(
        (registrationType) =>
          registrationType.agreementType === agreementType &&
          registrationType.formattedEntityNames === formattedEntityNames
      );
    if (
      selectedFundRegistrationType === null ||
      selectedFundRegistrationType === undefined
    ) {
      return {
        selectedRegistrationType: {},
      };
    }

    const {
      currentYearLimit,
      priorYearLimit,
      agreementKeyHouseholds,
      existingTickers,
    } = selectedFundRegistrationType;
    return {
      selectedRegistrationType: {
        agreementType,
        formattedEntityNames,
        modelAgreementUniqueIDs:
          selectedFundRegistrationType.modelAgreementUniqueIDs,
        existingTickers,
        classification: displayClassification,
        currentYearLimit,
        priorYearLimit,
        registrationGroup,
        typeEligiblePurchaseNew: true,
        productType,
        agreementKeyHouseholds,
      },
    };
  }
);

export const getExchangeToNewAvailableFundList = createSelector(
  getAllFundsList,
  getExchangeRegistrationTypeInfo,
  getExchangeToNewAllowedTickerLookup,
  getExchangeToNewFundList,
  (
    allFundsList,
    exchangeRegistrationTypeInfo,
    exchangeToNewAllowedTickerLookup,
    exchangeToNewFundList
  ) =>
    allFundsList
      .filter(
        (fund) => fund?.openToNewInvestors || fund?.openToSubsequentInvestments
      )
      .filter(
        (fund) => fund.merged !== true,
        ({ ticker }) => exchangeToNewAllowedTickerLookup[ticker]
        /*Enabling iclass for scs test work */
        // ({ iClass, ticker }) =>
        //   !iClass && exchangeToNewAllowedTickerLookup[ticker]
      )
      .map((fund) => {
        /** @type {AllFund} */
        const newFund = {
          ...fund,
          notes: {},
        };
        updateExchangeAddFundNotesForFund(
          newFund,
          exchangeToNewFundList,
          exchangeRegistrationTypeInfo.selectedRegistrationType
        );

        return newFund;
      })
      .sort(stringPropSorter((fund) => fund.name))
);

/**
 * Returns true/false indicating if the user can select multiple funds at once
 * as a source
 * @param state
 * @returns {boolean}
 */
export const getIsManySelectFrom = createSelector(
  getExchangeType,
  (exchangeType) => exchangeType === ExchangeType.MANY_TO_ONE
);

/**
 * Returns true/false indicating if the user can select multiple funds at once
 * as a destination
 * @param state
 * @returns {boolean}
 */
export const getIsManySelectTo = createSelector(
  getExchangeType,
  (exchangeType) => exchangeType === ExchangeType.ONE_TO_MANY
);

export const selectFederalWithholding = createStructuredSelector({
  federalWithholdingApplied: getExchangeFederalWithholdingApplied,
  federalWithholdingDollars: getExchangeFederalWithholdingDollars,
  federalWithholdingPercent: getExchangeFederalWithholdingPercent,
});

export const getElectionAgreementUniqueIds = (state) => {
  return {
    electionAgreementUniqueId1:
      state.exchangeTransaction.electionAgreementUniqueId1,
    electionAgreementUniqueId2:
      state.exchangeTransaction.electionAgreementUniqueId2,
  };
};

export const getAvailableFundsWithSameRegistrationGroup = createSelector(
  getExchangeFromLookup,
  getExchangeFromFundList,
  (exchangeLookUp, fromFundList) => {
    const fromLookUpSelected = Object.values(exchangeLookUp)?.filter(
      (fund) => fund.selected
    );
    return fromFundList?.filter(
      (fund) =>
        fund?.registrationGroup === fromLookUpSelected[0]?.registrationGroup
    );
  }
);

//takes users to from and compares the exchange type on selected funds
export const getAvailableToFundsWithSameRegistrationGroup = createSelector(
  getExchangeFromLookup,
  getExchangeToFundList,
  (exchangeLookUp, toFundList) => {
    const toLookUpSelected = Object.values(exchangeLookUp)?.filter(
      (fund) => fund.selected
    );
    return toFundList?.filter(
      (fund) =>
        fund?.registrationGroup === toLookUpSelected[0]?.registrationGroup
    );
  }
);
